/**
 * Task #1290 — Wizard padre scelta embedder (one-shot, blocking, irreversibile).
 *
 * Mostrato a full-screen al primo accesso del tenant finché
 * `tenant_settings.embedder_locked_at IS NULL`. L'utente DEVE scegliere
 * uno tra OpenAI / Gemini, poi confermare la scelta in un modale rosso
 * con checkbox di consapevolezza. Dopo POST /tenant/embedder-status/lock
 * con `confirm:true` la scelta è bloccata; eventuali cambi successivi
 * richiedono intervento master (re-indicizzazione completa KB).
 *
 * Copy verbatim da `.local/tasks/embedder-wizard-copy.md`. Modifiche al
 * testo richiedono aggiornamento contestuale del file di copy.
 */

import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@easyflow/ui-frontend/components/ui/dialog';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Checkbox } from '@easyflow/ui-frontend/components/ui/checkbox';
import { Loader2, AlertTriangle, FileText, Sparkles, Lock } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { embedderModelFor, toErr, type EmbedderLockInput } from '@easyflow/shared';
import apiClient from '@/lib/api';
import { useToast } from '@/hooks/use-toast';
import { EMBEDDER_STATUS_QUERY_KEY, dismissEmbedderWizardForSession } from '@/hooks/useEmbedderGate';

interface EmbedderChoiceWizardProps {
  open: boolean;
  onLocked: () => void;
  onDismiss?: () => void;
}

type Provider = EmbedderLockInput['provider'];

export default function EmbedderChoiceWizard({ open, onLocked, onDismiss }: EmbedderChoiceWizardProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [pendingProvider, setPendingProvider] = useState<Provider | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handlePick = (p: Provider) => {
    setPendingProvider(p);
    setConfirmed(false);
  };

  const handleCancelConfirm = () => {
    if (submitting) return;
    setPendingProvider(null);
    setConfirmed(false);
  };

  const handleConfirmLock = async () => {
    if (!pendingProvider || !confirmed) return;
    setSubmitting(true);
    try {
      const body: EmbedderLockInput = {
        provider: pendingProvider,
        model: embedderModelFor(pendingProvider),
        confirm: true,
      };
      await apiClient.post('/tenant/embedder-status/lock', body);
      await queryClient.invalidateQueries({ queryKey: EMBEDDER_STATUS_QUERY_KEY });
      toast({
        title: 'Embedder configurato',
        description: `Embedder ${pendingProvider === 'openai' ? 'OpenAI' : 'Gemini'} confermato. La configurazione AI consigliata è stata applicata: puoi modificare le singole funzioni dal Routing AI.`,
      });
      setPendingProvider(null);
      setConfirmed(false);
      onLocked();
    } catch (err: unknown) {
      const __err = toErr(err);
      toast({
        title: 'Errore',
        description: __err?.message || 'Lock embedder fallito',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* Full-screen blocking modal — non dismissibile (no ESC, no overlay click). */}
      <Dialog
        open={open}
        onOpenChange={(o) => {
          if (!o) {
            dismissEmbedderWizardForSession();
            onDismiss?.();
          }
        }}
      >
        <DialogContent
          className="!fixed !inset-0 !left-0 !top-0 !translate-x-0 !translate-y-0 !w-screen !h-screen !max-w-none !max-h-none !rounded-none !border-0 p-0 [&>button]:hidden flex flex-col items-center justify-center bg-background/95 backdrop-blur-sm overflow-hidden"
          data-testid="embedder-choice-wizard"
        >
          <div className="w-full max-w-5xl mx-auto rounded-xl border border-border bg-background shadow-2xl overflow-y-auto" style={{ maxHeight: 'calc(100vh - 2cm)', marginTop: '1cm', marginBottom: '1cm' }}>
          <DialogHeader className="px-6 pt-6 pb-3 border-b border-border bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-950 relative">
            <button
              type="button"
              onClick={() => {
                dismissEmbedderWizardForSession();
                onDismiss?.();
              }}
              className="absolute right-4 top-4 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors px-2 py-1 rounded border border-border bg-background/60 hover:bg-background"
              data-testid="embedder-wizard-dismiss"
            >
              Più tardi
            </button>
            <DialogTitle className="text-xl font-semibold flex items-center gap-2 pr-24">
              <Sparkles className="h-5 w-5 text-amber-500" />
              Configurazione iniziale knowledge base — Scegli il motore di ricerca AI
            </DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground mt-2">
              Per attivare la knowledge base e la ricerca documenti devi scegliere come l&apos;AI
              trasforma i tuoi contenuti in &quot;impronte numeriche&quot; per la ricerca semantica.
              <span className="block mt-1 font-medium text-foreground">
                Questa scelta è strutturale: definisce le capacità della pipeline RAG per il tuo tenant.
              </span>
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 p-6">
            {/* Colonna OpenAI */}
            <ChoiceCard
              provider="openai"
              title="OpenAI"
              subtitle="Solo testo · costo basso · query veloci"
              accent="sky"
              onSelect={() => handlePick('openai')}
            />
            {/* Colonna Gemini */}
            <ChoiceCard
              provider="gemini"
              title="Gemini"
              subtitle="Multimodale (testo + immagini + audio + video) · costo più alto · query più pesanti"
              accent="violet"
              onSelect={() => handlePick('gemini')}
            />
          </div>

          <div className="mx-6 mb-6 p-4 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800">
            <p className="text-sm text-amber-900 dark:text-amber-100">
              <strong className="font-semibold">Regola pratica.</strong>{' '}
              Se la tua attività vive di <strong>carta scansionata, foto di prodotti, audio o video</strong> → Gemini.
              Se vive di <strong>testo digitale (contratti, listini, manuali esportati)</strong> → OpenAI.
            </p>
          </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modale conferma rossa irreversibile */}
      <Dialog open={pendingProvider !== null} onOpenChange={(o) => { if (!o) handleCancelConfirm(); }}>
        <DialogContent
          className="max-w-md border-red-300 dark:border-red-800"
          onInteractOutside={(e) => { if (submitting) e.preventDefault(); }}
          onEscapeKeyDown={(e) => { if (submitting) e.preventDefault(); }}
          data-testid="embedder-confirm-modal"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600 dark:text-red-400">
              <AlertTriangle className="h-5 w-5" />
              Scelta irreversibile
            </DialogTitle>
            <DialogDescription className="text-sm text-foreground pt-2 leading-relaxed">
              Una volta indicizzato anche un solo documento con il provider scelto, cambiare
              embedder richiede di cancellare e re-indicizzare l&apos;intera knowledge base
              (il &quot;linguaggio numerico&quot; è incompatibile tra i due sistemi).
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm font-medium">
              Sei sicuro di voler procedere con{' '}
              <span className="font-bold text-red-600 dark:text-red-400">
                {pendingProvider === 'openai' ? 'OpenAI' : 'Gemini'}
              </span>?
            </p>
            <label className="mt-4 flex items-start gap-2 cursor-pointer">
              <Checkbox
                checked={confirmed}
                onCheckedChange={(v) => setConfirmed(v === true)}
                disabled={submitting}
                data-testid="embedder-confirm-checkbox"
                className="mt-0.5"
              />
              <span className="text-sm text-foreground">
                Ho letto e capisco che è irreversibile
              </span>
            </label>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={handleCancelConfirm} disabled={submitting}>
              Annulla
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmLock}
              disabled={!confirmed || submitting}
              data-testid="embedder-confirm-button"
            >
              {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Lock className="h-4 w-4 mr-2" />}
              Conferma scelta
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ---------------------------------------------------------------------------
// Carta scelta provider (colonna). Copy verbatim dal file `embedder-wizard-copy.md`.
// ---------------------------------------------------------------------------
interface ChoiceCardProps {
  provider: Provider;
  title: string;
  subtitle: string;
  accent: 'sky' | 'violet';
  onSelect: () => void;
}

function ChoiceCard({ provider, title, subtitle, accent, onSelect }: ChoiceCardProps) {
  const accentBorder = accent === 'sky' ? 'border-sky-200 dark:border-sky-900' : 'border-violet-200 dark:border-violet-900';
  const accentBg = accent === 'sky' ? 'bg-sky-50/40 dark:bg-sky-950/20' : 'bg-violet-50/40 dark:bg-violet-950/20';
  const accentBtn = accent === 'sky' ? 'bg-sky-600 hover:bg-sky-700' : 'bg-violet-600 hover:bg-violet-700';
  const accentText = accent === 'sky' ? 'text-sky-700 dark:text-sky-300' : 'text-violet-700 dark:text-violet-300';
  const Icon = provider === 'openai' ? FileText : Sparkles;

  return (
    <div
      className={`flex flex-col rounded-xl border-2 ${accentBorder} ${accentBg} p-5 transition-all hover:shadow-md`}
      data-testid={`embedder-card-${provider}`}
    >
      <div className="flex items-center gap-3 mb-3">
        <div className={`p-2 rounded-lg ${accent === 'sky' ? 'bg-sky-100 dark:bg-sky-900/40' : 'bg-violet-100 dark:bg-violet-900/40'}`}>
          <Icon className={`h-5 w-5 ${accentText}`} />
        </div>
        <div>
          <h3 className={`text-lg font-bold ${accentText}`}>{title}</h3>
          <p className="text-xs text-muted-foreground">{subtitle}</p>
        </div>
      </div>

      <div className="space-y-3 text-sm flex-1">
        {provider === 'openai' ? (
          <>
            <Section title="Come funziona, in due righe">
              Trasforma il testo dei tuoi documenti in &quot;impronte numeriche&quot;. Quando fai una
              domanda, cerca le impronte più simili a quella della domanda e ti restituisce i pezzi
              di documento corrispondenti. Lavora <strong>solo sul testo</strong>: tutto il resto
              (foto, audio, video, scansioni) deve essere prima convertito in testo da un altro
              strumento.
            </Section>
            <Section title="Use case ideali">
              <ul className="list-disc pl-5 space-y-0.5">
                <li>Studio legale che indicizza contratti, sentenze, normative</li>
                <li>Assistenza clienti su manuali e FAQ</li>
                <li>Knowledge base interna aziendale (procedure, policy, listini)</li>
                <li>E-commerce con schede prodotto testuali (descrizioni, specifiche)</li>
              </ul>
            </Section>
            <Section title="Pro" tone="pro">
              <ul className="list-disc pl-5 space-y-0.5">
                <li>Costo per documento basso e prevedibile</li>
                <li>Velocità costante anche con archivi molto grandi</li>
                <li>Risposte testuali precise su domini ben definiti</li>
              </ul>
            </Section>
            <Section title="Contro" tone="con">
              <ul className="list-disc pl-5 space-y-0.5">
                <li>Non &quot;vede&quot; immagini né &quot;ascolta&quot; audio/video</li>
                <li>I PDF scansionati richiedono un passaggio di conversione separato (più lento, costo extra, qualche errore)</li>
                <li>Foto di scontrini, documenti d&apos;identità, cartellini → invisibili</li>
              </ul>
            </Section>
            <Section title="Cosa succede dopo">
              <ul className="list-disc pl-5 space-y-0.5">
                <li><strong>Configurazione consigliata automatica</strong>: tutte le funzioni AI (risposte, OCR, lettura documenti, voce, builder) vengono impostate su OpenAI con i modelli adatti a ciascuna</li>
                <li><strong>Modifiche per funzione</strong>: dal Routing AI puoi scegliere un altro provider o modello per le singole funzioni; sintesi e scomposizione delle risposte seguono il modello chat della pipeline RAG</li>
              </ul>
            </Section>
          </>
        ) : (
          <>
            <Section title="Come funziona, in due righe">
              Crea le stesse &quot;impronte numeriche&quot;, ma le sa fare anche partendo da{' '}
              <strong>immagini, audio, video e pagine scansionate</strong> — non solo testo. Una
              domanda scritta può quindi recuperare anche una foto pertinente, perché tutto vive
              nello stesso spazio di significato.
            </Section>
            <Section title="Use case ideali">
              <ul className="list-disc pl-5 space-y-0.5">
                <li>Punto vendita / retail che indicizza foto prodotti, cataloghi visivi, scaffali</li>
                <li>Studio architetti / immobiliare con planimetrie, render, foto cantiere</li>
                <li>Officina o assistenza tecnica che archivia foto di pezzi, schermate diagnostiche</li>
                <li>Aziende con tanto cartaceo digitalizzato (fatture firmate, ricevute, moduli compilati a mano)</li>
                <li>Settori che producono o ricevono note vocali e brevi video di servizio</li>
              </ul>
            </Section>
            <Section title="Pro" tone="pro">
              <ul className="list-disc pl-5 space-y-0.5">
                <li>L&apos;agente &quot;vede&quot; le immagini e &quot;ascolta&quot; gli audio davvero</li>
                <li>I PDF scansionati vengono indicizzati direttamente, senza passaggio OCR separato</li>
                <li>Una sola query testuale può recuperare anche immagini (&quot;trova il modello con la maniglia in legno&quot;)</li>
              </ul>
            </Section>
            <Section title="Contro" tone="con">
              <ul className="list-disc pl-5 space-y-0.5">
                <li>Costo per documento sensibilmente più alto: paghi l&apos;analisi multimediale (immagini, audio, video) anche quando il contenuto è testuale</li>
                <li>Le query sulla knowledge base sono più pesanti perché ogni documento occupa più spazio nel &quot;dizionario numerico&quot;</li>
                <li>Su archivi oltre 10.000 documenti per agente la ricerca rallenta (limite tecnico in via di risoluzione)</li>
                <li>Limiti per file: audio max 3 minuti, video max 1 minuto e 20 secondi</li>
              </ul>
            </Section>
            <Section title="Cosa succede dopo">
              <ul className="list-disc pl-5 space-y-0.5">
                <li><strong>Configurazione consigliata automatica</strong>: le funzioni AI vengono impostate su Gemini con i modelli adatti a ciascuna, per restare nello stesso &quot;spazio di significato&quot; dell&apos;embedder; voce e trascrizioni passano a OpenAI dove Gemini non offre un modello adatto</li>
                <li><strong>Contenuti multimodali</strong>: se lo storage dei file è configurato, immagini, pagine e media vengono indicizzati per i nuovi caricamenti (i documenti già presenti non vengono rielaborati)</li>
                <li><strong>Modifiche per funzione</strong>: dal Routing AI puoi scegliere un altro provider o modello per le singole funzioni; sintesi e scomposizione delle risposte seguono il modello chat della pipeline RAG</li>
              </ul>
            </Section>
          </>
        )}
      </div>

      <Button
        onClick={onSelect}
        className={`mt-5 w-full text-white ${accentBtn}`}
        data-testid={`embedder-pick-${provider}`}
      >
        Scegli {title}
      </Button>
    </div>
  );
}

function Section({ title, tone, children }: { title: string; tone?: 'pro' | 'con'; children: React.ReactNode }) {
  const toneClass = tone === 'pro'
    ? 'text-emerald-700 dark:text-emerald-400'
    : tone === 'con'
      ? 'text-rose-700 dark:text-rose-400'
      : 'text-foreground';
  return (
    <div>
      <p className={`text-xs font-semibold uppercase tracking-wide mb-1 ${toneClass}`}>{title}</p>
      <div className="text-sm text-foreground/90 leading-relaxed">{children}</div>
    </div>
  );
}
